<!-- src/views/HomeView.vue -->
<template>
  <div class="home-view-container">
    <!-- Top Navigation Bar -->
    <header class="home-nav">
      <div class="d-flex align-center ga-3">
        <v-icon icon="mdi-play-box-multiple" color="primary" size="28" />
        <div class="brand-title">
          <span class="text-white font-weight-bold">SKO</span>
          <span class="text-primary font-weight-bold">Preview</span>
          
        </div>
        <div class="brand-title-side">
          {{ appVersion }}
        </div>
      </div>

      <div class="d-flex align-center ga-2">
        <!-- Language Switcher Toggle -->
        <v-btn
          variant="tonal"
          size="small"
          color="primary"
          prepend-icon="mdi-translate"
          class="text-none font-weight-bold"
          :title="locale === 'th' ? 'Switch to English' : 'เปลี่ยนเป็นภาษาไทย'"
          @click="toggleLocale"
        >
          {{ locale === 'th' ? 'TH | EN' : 'EN | TH' }}
        </v-btn>

        <v-btn
          variant="tonal"
          size="small"
          prepend-icon="mdi-server-network"
          href="https://skoservice.akawatmor.com"
          target="_blank"
          class="text-none"
        >
          {{ t('nav.hubBtn') }}
        </v-btn>
      </div>
    </header>

    <!-- Hero Header -->
    <section class="hero-section text-center">
      <h1 class="hero-title">
        {{ t('hero.title') }}

        <template v-if="t('hero.title-s')">
          <br />
          {{ t('hero.title-s') }}
        </template>
      </h1>
      <p class="hero-subtitle">
        {{ t('hero.subtitle') }}
      </p>
    </section>

    <!-- Main Action Selector Tabs / Cards -->
    <main class="action-card-container">
      <v-card class="main-hub-card" elevation="10" rounded="xl">
        <v-tabs
          v-model="activeTab"
          color="primary"
          grow
          class="hub-tabs"
        >
          <v-tab value="link" class="text-none py-2 px-2 px-sm-4">
            <v-icon icon="mdi-link-variant" start size="18" />
            <span class="tab-label">{{ t('selectfile.tabs.link') }}</span>
          </v-tab>
          <v-tab value="upload" class="text-none py-2 px-2 px-sm-4">
            <v-icon icon="mdi-cloud-upload" start size="18" />
            <span class="tab-label">{{ t('selectfile.tabs.upload') }}</span>
          </v-tab>
          <v-tab value="public" class="text-none py-2 px-2 px-sm-4">
            <v-icon icon="mdi-folder-network" start size="18" />
            <span class="tab-label">{{ t('selectfile.tabs.public') }}</span>
          </v-tab>
        </v-tabs>

        <v-window v-model="activeTab" class="hub-window pa-4 pa-sm-6">
          <!-- Tab 1: Direct Link -->
          <v-window-item value="link">
            <div class="tab-content py-2 py-sm-4">
              <div class="text-subtitle-1 font-weight-bold mb-1">
                {{ t('selectfile.link.title') }}
              </div>
              <div class="text-caption text-medium-emphasis mb-4">
                {{ t('selectfile.link.subtitle') }}
              </div>

              <div class="d-flex ga-3 flex-column flex-sm-row">
                <v-text-field
                  v-model="linkInput"
                  variant="outlined"
                  density="comfortable"
                  :placeholder="t('selectfile.link.placeholder')"
                  prepend-inner-icon="mdi-web"
                  clearable
                  hide-details
                  class="flex-grow-1"
                  @keydown.enter="previewDirectLink"
                />
                <v-btn
                  color="primary"
                  size="large"
                  prepend-icon="mdi-eye"
                  class="px-6 text-none font-weight-bold w-100 w-sm-auto"
                  :disabled="!linkInput.trim()"
                  @click="previewDirectLink"
                >
                  {{ t('selectfile.link.button') }}
                </v-btn>
              </div>

              <!-- Quick Examples -->
              <div class="quick-links mt-6 pt-2 border-t">
                <span class="text-caption text-medium-emphasis mr-2">{{ t('selectfile.link.samplesTitle') }}</span>
                <div
                  v-for="type in sampleLinksType"
                  :key="type.type"
                  class="py-1"
                >
                  <span class="pr-2">{{ locale === 'th' ? type.nameTH : (t('sampleTypes.' + type.type) || type.name) }}:</span>
                  <template v-for="sample in sampleLinks" :key="sample.name">
                    <v-chip
                      v-if="sample.type === type.type"
                      size="small"
                      variant="outlined"
                      class="mr-2 mb-1 cursor-pointer"
                      @click="linkInput = sample.path"
                      :aria-label="sample.ariaLabel"
                    >
                      <v-icon :icon="sample.icon" start size="14" />
                      {{ sample.name }}
                    </v-chip>
                  </template>
                </div>
              </div>
            </div>
          </v-window-item>

          <!-- Tab 2: Upload File -->
          <v-window-item value="upload">
            <div class="tab-content py-2 py-sm-4">
              <div
                class="drop-zone pa-5 pa-sm-8 text-center"
                :class="{ 'is-dragover': isDragOver }"
                @dragover.prevent="isDragOver = true"
                @dragleave.prevent="isDragOver = false"
                @drop.prevent="handleFileDrop"
                @click="triggerFileInput"
              >
                <input
                  ref="fileInputRef"
                  type="file"
                  class="d-none"
                  @change="handleFileSelected"
                />
                <v-icon icon="mdi-tray-arrow-up" size="48" color="primary" class="mb-2 mb-sm-3" />
                <div class="text-subtitle-1 text-sm-h6 font-weight-bold text-slate-100">
                  {{ t('selectfile.upload.dropTitle') }}
                </div>
                <div class="text-caption text-medium-emphasis mt-1">
                  {{ t('selectfile.upload.dropSubtitle') }} <br>
                  <p style="font-size: 10px;">{{ t('selectfile.upload.privacyNote') }}</p>
                </div>
              </div>
            </div>
          </v-window-item>

          <!-- Tab 3: SKO Public Files Explorer -->
          <v-window-item value="public">
            <div class="public-files-view">
              <div class="d-flex align-center justify-between mb-3 px-1">
                <div>
                  <div class="text-subtitle-1 font-weight-bold">
                    {{ t('selectfile.public.title') }}
                  </div>
                  <div class="text-caption text-medium-emphasis">
                    {{ t('selectfile.public.subtitle') }}
                  </div>
                </div>
              </div>

              <!-- Live Folder Explorer Component -->
              <div class="embedded-explorer-wrapper">
                <FolderExplorer @select-file="handlePublicFileSelect" />
              </div>
            </div>
          </v-window-item>
        </v-window>
      </v-card>
    </main>

    <!-- Feature Highlights Section -->
    <section class="features-section py-10 px-4">
      <div class="text-center mb-8">
        <h2 class="section-title">{{ t('features.title') }}</h2>
        <p class="text-caption text-medium-emphasis">{{ t('features.subtitle') }}</p>
      </div>

      <div class="feature-grid">
        <v-card
          v-for="feat in featureHighlights"
          :key="feat.title"
          class="feature-card pa-5 d-flex flex-column"
          rounded="lg"
        >
          <div class="d-flex align-center justify-space-between mb-3">
            <v-icon :icon="feat.icon" :color="feat.color" size="32" />
            <v-chip size="x-small" variant="tonal" :color="feat.color" class="font-weight-bold">
              {{ locale === 'th' ? feat.tag : (feat.tagEn || feat.tag) }}
            </v-chip>
          </div>
          <div class="text-subtitle-1 font-weight-bold mb-1">
            {{ locale === 'th' ? feat.title : (feat.titleEn || feat.title) }}
          </div>
          <p class="text-caption text-medium-emphasis flex-grow-1" style="line-height: 1.6;">
            {{ locale === 'th' ? feat.description : (feat.descriptionEn || feat.description) }}
          </p>
        </v-card>
      </div>
    </section>

    <!-- Footer -->
    <footer class="home-footer text-center py-6 text-caption text-medium-emphasis border-t">
      <div>
        {{ t('footer.copyright') }}
      </div>
      <div>
        {{ t('footer.disclaimer') }}
      </div>
      <div>
        {{appVersionFull}}
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { usePreviewStore } from '../stores/previewStore.js'
import { useI18n } from '../composables/useI18n.js'
import FolderExplorer from '../components/common/FolderExplorer.vue'
import {
  formatToRelativePath,
  getCompatibleTypes,
  getExtension
} from '../utils/skoUrlResolver.js'

const router = useRouter()
const store = usePreviewStore()
const { t, locale, toggleLocale } = useI18n()

const activeTab = ref('link')
const linkInput = ref('')
const isDragOver = ref(false)
const fileInputRef = ref(null)
const appVersion = __APP_VERSION_NUMBER__ + " " + __APP_VERSION_CODE__
const appVersionFull =  __APP_VERSION_NUMBER__ + " " +  __APP_VERSION_CODE__ + " Codename " +  __APP_VERSION_CODENAME__ 
const sampleLinksType = [
  {
    type: 'Audio',
    name: 'Audio',
    nameTH: 'เสียง',
  },
  {
    type: 'Video',
    name: 'Video',
    nameTH: 'วิดีโอ',
  },
  {
    type: 'Images',
    name: 'Images',
    nameTH: 'ภาพ',
  },
  {
    type: 'Document',
    name: 'Document',
    nameTH: 'เอกสาร',
  }
]
const sampleLinks = [
  { 
    name: 'Audio',
    type: 'Audio',
    path: 'Audio/Music/Backtraxx%20Music%20Library%201%20Full%20Length/013%20Carmen%20Overture.m4a', 
    icon: 'mdi-music', 
    ariaLabel: 'Play Carmen Overture Sample from Publicshare' 
  },
  { 
    name: 'Stem',
    type: 'Audio',
    path: 'Audio/Music/Stacktraxx%20Music%20Library%20Vol.37%20Jazz/Confusion_Full.stem.m4a', 
    icon: 'mdi-tune-vertical', 
    ariaLabel: 'Play Stem Sample from Publicshare' 
  },
  { 
    name: 'MIDI',
    type: 'Audio',
    path: 'Audio/MIDI/0000.mid', 
    icon: 'mdi-piano', 
    ariaLabel: 'Play HBD from Publicshare' 
  },
  { 
    name: 'Video',
    type: 'Video',
    path: 'Video/Test/5.1HEAAC160KbpsH264Test-ChID-BLITS-EBU.mp4', 
    icon: 'mdi-video', 
    ariaLabel: 'Play HEAAC Test Video from Publicshare' 
  },
  { 
    name: 'Public Share Root',
    type: 'Public Share Root',
    path: 'https://skoservice.akawatmor.com/files/', 
    icon: 'mdi-folder', 
    ariaLabel: 'Open Public Share Root' 
  }
]

const featureHighlights = [
  {
    icon: 'mdi-tune-vertical',
    color: 'cyan',
    tag: 'Audio / Stem',
    tagEn: 'Audio / Stem',
    title: 'Stem Multi-Track Mixer',
    titleEn: 'Stem Multi-Track Mixer',
    description: 'แยกแทร็กหลายชิ้น เช่น Vocals, Drums, Bass และดนตรีอื่น ๆ จากไฟล์ .stem.mp4 / .stem.m4a แบบเรียลไทม์ ปรับ Volume Boost สูงสุด 300%, Mute, Solo แยกชิ้น พร้อมวัดระดับสัญญาณด้วย True RMS VU Meter',
    descriptionEn: 'Real-time multi-track isolation for Vocals, Drums, Bass, and instruments from .stem.mp4 / .stem.m4a. Up to 300% volume boost, per-channel solo/mute, and True RMS VU meters.'
  },
  {
    icon: 'mdi-piano',
    color: 'purple',
    tag: 'Music / Karaoke',
    tagEn: 'Music / Karaoke',
    title: 'MIDI + SoundFont Karaoke',
    titleEn: 'MIDI + SoundFont Karaoke',
    description: 'ซินธิไซเซอร์จำลองเสียงเครื่องดนตรีระดับสูง รองรับ SoundFont (.sf3/.sf2) แสดงผลเนื้อร้องคาราโอเกะ (.kar / .mid) แบบสด พร้อมแอนิเมชันลูกบอลวิ่งตามตัวอักษรและพรีวิวโน้ตเปียโน',
    descriptionEn: 'High-grade software synthesizer supporting SoundFonts (.sf2/.sf3) with live synced karaoke lyrics (.kar/.mid), bouncing ball animation, and real-time piano roll notes preview.'
  },
  {
    icon: 'mdi-gamepad-variant',
    color: 'amber',
    tag: 'Demoscene / Chiptune',
    tagEn: 'Demoscene / Chiptune',
    title: 'Retro Tracker & Chiptune',
    titleEn: 'Retro Tracker & Chiptune',
    description: 'จำลองชิปเสียงเล่นเพลงสไตล์เกมย้อนยุคและ Demoscene ซัพพอร์ตไฟล์เพลงโมดูลยอดนิยมทั้ง .mod, .xm, .it, .s3m ผ่าน Soundchip WebAudio ในตัว',
    descriptionEn: 'Authentic retro gaming & demoscene soundchip emulation. Plays tracker module files including .mod, .xm, .it, and .s3m via built-in WebAudio engines.'
  },
  {
    icon: 'mdi-video-vintage',
    color: 'blue',
    tag: 'Video / Streaming',
    tagEn: 'Video / Streaming',
    title: 'Artplayer 4K & HLS Live Stream',
    titleEn: 'Artplayer 4K & HLS Live Stream',
    description: 'เครื่องเล่นวิดีโอความละเอียดสูง รองรับ .mp4, .mkv, .webm และสตรีมมิ่งสด .m3u8 (HLS) พร้อมฟังก์ชัน PiP ลอยเหนือหน้าต่างอื่น, ปรับความเร็ว, ซับไตเติล และตัวตรวจจับ MediaInfo ละเอียด',
    descriptionEn: 'High-definition video player supporting .mp4, .mkv, .webm and live .m3u8 (HLS) streams with floating Picture-in-Picture (PiP), speed controls, subtitles, and deep MediaInfo probing.'
  },
  {
    icon: 'mdi-file-document-multiple',
    color: 'indigo',
    tag: 'Office Documents',
    tagEn: 'Office Documents',
    title: 'Universal Office Viewer',
    titleEn: 'Universal Office Viewer',
    description: 'อ่านไฟล์เอกสาร PDF, Word (.docx), สเปรดชีต Excel (.xlsx) รองรับการสลับชีตงาน รวมถึงพรีวิวรูปภาพความละเอียดสูง ซูมหมุนภาพได้ทันทีโดยไม่ต้องติดตั้งโปรแกรมภายนอก',
    descriptionEn: 'View PDF, Word (.docx, .doc), and Excel (.xlsx, .xls) spreadsheets with instant sheet switching, high-res image zoom and rotate without installing any external software.'
  },
  {
    icon: 'mdi-language-markdown',
    color: 'teal',
    tag: 'Technical Docs',
    tagEn: 'Technical Docs',
    title: 'Markdown, Math LaTeX & Mermaid',
    titleEn: 'Markdown, Math LaTeX & Mermaid',
    description: 'แสดงผลเอกสาร Markdown (.md) รองรับการถอดรหัสสูตรคณิตศาสตร์ KaTeX และเรนเดอร์ไดอะแกรม Mermaid.js (Flowcharts, Git Graph, Sequence, Architecture, Venn) แบบอินเทอร์แอคทีฟ',
    descriptionEn: 'Renders Markdown (.md) documents with KaTeX mathematical formulas and interactive Mermaid.js diagrams (Flowcharts, Git Graph, Sequence, Architecture, Venn).'
  },
  {
    icon: 'mdi-database-search',
    color: 'light-blue',
    tag: 'Data / Analytics',
    tagEn: 'Data / Analytics',
    title: 'Database & Modern Datasets',
    titleEn: 'Database & Modern Datasets',
    description: 'เปิดดูและเขียนคิวรี SQL บนฐานข้อมูล SQLite (.db, .sqlite), ตรวจสอบโครงสร้างสกีมาและสถิติข้อมูลไฟล์ Apache Parquet (.parquet), .csv, .tsv และ .json ได้ทันทีบนเครื่องของคุณ',
    descriptionEn: 'Inspect and query SQLite databases (.db, .sqlite) directly in browser, explore schemas and statistics for Apache Parquet (.parquet), .csv, .tsv, and .json files.'
  },
  {
    icon: 'mdi-folder-zip',
    color: 'orange',
    tag: 'Archive / Package',
    tagEn: 'Archive / Package',
    title: 'Universal Archive Explorer',
    titleEn: 'Universal Archive Explorer',
    description: 'ส่องโครงสร้างไฟล์ภายในแฟ้มบีบอัด .zip, .7z, .rar, .tar, .gz โดยไม่ต้องอัปโหลดขึ้นเซิร์ฟเวอร์ สามารถค้นหา สตรีมไฟล์ และดาวน์โหลดแยกชิ้นได้อย่างปลอดภัย',
    descriptionEn: 'Explore inside compressed archives (.zip, .7z, .rar, .tar, .gz) without uploading to servers. Safely search, stream, and extract individual files locally.'
  },
  {
    icon: 'mdi-harddisk',
    color: 'red',
    tag: 'Virtualization',
    tagEn: 'Virtualization',
    title: 'Disk Image & x86 PC Emulator',
    titleEn: 'Disk Image & x86 PC Emulator',
    description: 'ตรวจสอบโครงสร้างพาร์ติชันและเซกเตอร์ของไฟล์ดิสก์ .iso, .img, .vhd พร้อมมี v86 Virtual Machine จำลองเครื่องคอมพิวเตอร์ x86 บูตระบบปฏิบัติการสดๆ บนเว็บได้ทันที',
    descriptionEn: 'Analyze partition and sector layouts of disk images (.iso, .img, .vhd), paired with an embedded v86 Virtual Machine to boot x86 OS live in the browser.'
  },
  {
    icon: 'mdi-code-braces',
    color: 'green',
    tag: 'Developer',
    tagEn: 'Developer',
    title: 'Code Runner & Syntax Highlighter',
    titleEn: 'Code Runner & Syntax Highlighter',
    description: 'ไฮไลท์ไวยากรณ์ซอร์สโค้ดมากกว่า 100 ภาษา พร้อมความสามารถในการทดสอบรันโค้ดภาษา Python (ผ่าน MicroPython WebAssembly) และภาษา C++ (ผ่าน JSCPP) ได้โดยตรงบนเบราว์เซอร์',
    descriptionEn: 'Syntax highlighting for over 100 languages with in-browser execution for Python (via MicroPython WebAssembly) and C++ (via JSCPP).'
  },
  {
    icon: 'mdi-format-font',
    color: 'pink',
    tag: 'Typography',
    tagEn: 'Typography',
    title: 'Font Typography Inspector',
    titleEn: 'Font Typography Inspector',
    description: 'ทดสอบพิมพ์ข้อความ พรีวิวฟอนต์ตัวอักษร .ttf, .otf, .woff, .woff2, .eot พร้อมเครื่องมือตรวจสเกล Glyph Vector, รายการตาราง OpenType และฟีเจอร์ตัวอักษรครบวงจร',
    descriptionEn: 'Interactive text testing and preview for .ttf, .otf, .woff, .woff2, .eot fonts with vector glyph scaling, OpenType tables inspector, and typography metrics.'
  },
  {
    icon: 'mdi-matrix',
    color: 'deep-orange',
    tag: 'Engineering',
    tagEn: 'Engineering',
    title: 'Binary Hex & Casio Program',
    titleEn: 'Binary Hex & Casio Program',
    description: 'เจาะลึกโครงสร้างไบนารีระดับไบต์ผ่าน Hex Inspector พร้อมรองรับการถอดรหัสและแสดงผลซอร์สโค้ดโปรแกรมเครื่องคิดเลขวิทยาศาสตร์ Casio (.g1m, .g2m)',
    descriptionEn: 'Byte-level binary inspection with Hex Inspector, plus decoding and syntax highlighting for Casio scientific calculator programs (.g1m, .g2m).'
  }
]

function previewDirectLink() {
  const input = linkInput.value.trim()
  if (!input) return

  // Clear previous uploaded file so pasted URL resolves cleanly
  store.clearUploadedFile()

  // Format to relative path if on SKO public files
  const queryUrl = formatToRelativePath(input)
  const comp = getCompatibleTypes(queryUrl)

  router.push({
    path: '/preview',
    query: {
      url: queryUrl,
      type: comp.defaultType || 'audio'
    }
  })
}

function triggerFileInput() {
  fileInputRef.value?.click()
}

function handleFileSelected(e) {
  const file = e.target.files?.[0]
  if (file) processUploadFile(file)
}

function handleFileDrop(e) {
  isDragOver.value = false
  const file = e.dataTransfer.files?.[0]
  if (file) processUploadFile(file)
}

function processUploadFile(file) {
  const blobUrl = store.registerUploadedFile(file)
  const comp = getCompatibleTypes(file.name)

  router.push({
    path: '/preview',
    query: {
      url: blobUrl,
      name: file.name,
      type: comp.defaultType || 'audio'
    }
  })
}

function handlePublicFileSelect(fileItem) {
  store.clearUploadedFile()
  const isPlaylist = fileItem.defaultType === 'playlist' || ['m3u', 'm3u8', 'pls', 'cue', 'xspf', 'asx', 'wpl'].includes(getExtension(fileItem.name || fileItem.relativePath))
  if (isPlaylist && fileItem.siblingItems && Array.isArray(fileItem.siblingItems)) {
    store.setPlaylist(fileItem.siblingItems, 0, { isActualPlaylist: true })
  } else {
    store.clearPlaylist()
  }
  const encodedRel = encodeURIComponent(fileItem.relativePath)
  router.push({
    path: '/preview',
    query: {
      url: encodedRel,
      type: fileItem.defaultType || 'audio'
    }
  })
}

onMounted(() => {
  store.clearUploadedFile()
  store.clearPlaylist()
})
</script>

<style scoped>
.home-view-container {
  height: 100%;
  min-height: 100vh;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  background: radial-gradient(circle at 50% 0%, #171f33 0%, #090d16 65%);
  color: var(--text-main, #f1f5f9);
  display: flex;
  flex-direction: column;
}

.home-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  padding-top: calc(14px + env(safe-area-inset-top, 0px));
  padding-left: calc(16px + env(safe-area-inset-left, 0px));
  padding-right: calc(16px + env(safe-area-inset-right, 0px));
  background: rgba(15, 23, 42, 0.8);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.brand-title {
  font-size: 19px;
  letter-spacing: -0.3px;
}

.hero-section {
  padding: 50px 20px 24px;
  max-width: 800px;
  margin: 0 auto;
}

.hero-title {
  font-size: clamp(24px, 4vw, 38px);
  font-weight: 800;
  line-height: 1.25;
  background: linear-gradient(135deg, #ffffff 40%, #94a3b8 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 12px;
}

.hero-subtitle {
  font-size: 15px;
  color: #94a3b8;
  max-width: 620px;
  margin: 0 auto;
  line-height: 1.6;
}

.action-card-container {
  max-width: 960px;
  width: 95%;
  margin: 0 auto;
  flex: 1;
}

.main-hub-card {
  background: rgba(17, 24, 39, 0.85) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
}

.hub-tabs {
  background: rgba(15, 23, 42, 0.6);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.drop-zone {
  border: 2px dashed rgba(59, 130, 246, 0.35);
  border-radius: 14px;
  background: rgba(59, 130, 246, 0.03);
  cursor: pointer;
  transition: all 0.2s ease;
}

.drop-zone:hover, .drop-zone.is-dragover {
  border-color: #3b82f6;
  background: rgba(59, 130, 246, 0.08);
  transform: scale(1.005);
}

.embedded-explorer-wrapper {
  height: 480px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.features-section {
  max-width: 1060px;
  margin: 0 auto;
  width: 95%;
}

.section-title {
  font-size: 22px;
  font-weight: 700;
  color: #f8fafc;
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
}

.feature-card {
  background: rgba(23, 31, 51, 0.6) !important;
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: transform 0.2s ease, border-color 0.2s ease;
}

.feature-card:hover {
  transform: translateY(-3px);
  border-color: rgba(59, 130, 246, 0.3);
}

.home-footer {
  background: rgba(10, 14, 23, 0.9);
  border-color: rgba(255, 255, 255, 0.05) !important;
  padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px));
  font-size: 10px;
}

@media (max-width: 640px) {
  .home-nav {
    padding: 12px 14px;
  }
  .hero-section {
    padding: 24px 14px 16px;
  }
  .hero-title {
    font-size: 22px;
    margin-bottom: 8px;
  }
  .hero-subtitle {
    font-size: 13px;
  }
  .action-card-container {
    width: 100%;
    padding: 0 10px;
  }
  .tab-label {
    font-size: 12px;
  }
  .embedded-explorer-wrapper {
    height: 380px;
  }
  .features-section {
    padding-top: 24px !important;
    padding-bottom: 24px !important;
  }
  .feature-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}
</style>
